import { Compass } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest } from "../lib/api";
import { useGallery } from "../lib/queries";
import type { ApiEnvelope, User } from "../types";
import { Logo } from "./Logo";
import { PRODUCT_ICONS } from "../lib/product-icons";
import { Modal } from "./Modal";

/**
 * "Pop-up de bienvenida": a five-screen tour of the app in a frosted card over a destination photo,
 * from the design of the same name. It opens the first time a customer reaches the app after
 * signing in and is recorded on the account (`welcomeSeen`) when finished or skipped, so another
 * device does not replay it. Perfil offers "Ver el recorrido otra vez".
 */

export interface TourSlide {
  id: string;
  title: string;
  copy: string;
  icon: ReactNode;
}

/** The product slides use the product cards' icons, so a product looks the same in the tour and in Finanzas. */
function TourIcon({ product }: { product: "wallet" | "luggage" | "plane" }) {
  const Icon = PRODUCT_ICONS[product]!;
  return <Icon size={34} strokeWidth={1.75} aria-hidden="true" />;
}

export const TOUR_SLIDES: TourSlide[] = [
  {
    id: "bienvenida",
    title: "Bienvenido a Aventuria",
    copy: "La super app del viajero.",
    icon: <img src="/isotipo-cropped.png" alt="" className="welcome-isotipo" />
  },
  {
    id: "explorar",
    title: "Viajes",
    copy: "Encuentra inspiración, destinos y experiencias para tu próxima aventura.",
    icon: <Compass size={34} strokeWidth={1.75} aria-hidden="true" />
  },
  {
    id: "wallet-libre",
    title: "Wallet Libre",
    copy: "Aquí podrás ahorrar para tus viajes. Puedes retirar tus fondos cualquier día hábil.",
    icon: <TourIcon product="wallet" />
  },
  {
    id: "wallet-aventuria",
    title: "Apartado",
    copy: "Tu próximo viaje, a tu ritmo. Aparta con solo $9,000 pesos por pasajero, abona cuánto y cuándo quieras.",
    icon: <TourIcon product="luggage" />
  },
  {
    id: "tnpl",
    title: "Viaja ahora, paga después",
    copy: "Te prestamos para viajar hoy con una tasa preferencial. Tú lo pagas a tu ritmo.",
    icon: <TourIcon product="plane" />
  }
];

export const tourHelpers = {
  isLast: (step: number, count = TOUR_SLIDES.length) => step >= count - 1,
  ctaLabel: (step: number, count = TOUR_SLIDES.length) => (tourHelpers.isLast(step, count) ? "Empezar a explorar" : "Continuar"),
  stepLabel: (step: number) => String(step + 1).padStart(2, "0"),
  /** The next step after "Continuar", or null when the tour is over. */
  advance: (step: number, count = TOUR_SLIDES.length) => (tourHelpers.isLast(step, count) ? null : step + 1)
};

interface WelcomeTourContextValue { open(): void }
const WelcomeTourContext = createContext<WelcomeTourContextValue | null>(null);

/** Hosts the tour for everything inside the shell and exposes `open()` to replay it. */
export function WelcomeTourProvider({ children }: { children: ReactNode }) {
  const { user, applyUser } = useAuth();
  const [open, setOpen] = useState(false);
  // Which account the automatic first-visit opening was decided for, so a sign-out and sign-in as
  // someone else in the same tab is evaluated afresh. Recorded only once the timer fires: an effect
  // that is cleaned up before then (StrictMode's double mount, a quick re-render) must not count.
  const decidedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user || decidedFor.current === user.id) return;
    if (user.welcomeSeen) { decidedFor.current = user.id; return; }
    // Let the page paint first so the card rises over the app rather than over a blank screen.
    const timer = window.setTimeout(() => { decidedFor.current = user.id; setOpen(true); }, 350);
    return () => window.clearTimeout(timer);
  }, [user]);

  const close = useCallback(() => {
    setOpen(false);
    if (!user || user.welcomeSeen) return;
    // Optimistic: the account is updated locally at once so nothing re-opens the tour, and the API
    // records it in the background. If that fails the tour simply plays again next time.
    applyUser({ ...user, welcomeSeen: true });
    apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/profile", { method: "PATCH", body: JSON.stringify({ welcomeSeen: true }) })
      .then((payload) => applyUser(payload.data.user))
      .catch(() => undefined);
  }, [user, applyUser]);

  const value = useMemo(() => ({ open: () => setOpen(true) }), []);
  return (
    <WelcomeTourContext.Provider value={value}>
      {children}
      <WelcomeTour open={open} onClose={close} />
    </WelcomeTourContext.Provider>
  );
}

export function useWelcomeTour(): WelcomeTourContextValue {
  const context = useContext(WelcomeTourContext);
  if (!context) throw new Error("useWelcomeTour must be used within WelcomeTourProvider");
  return context;
}

export function WelcomeTour({ open, onClose }: { open: boolean; onClose(): void }) {
  const [step, setStep] = useState(0);
  const gallery = useGallery();
  const photo = gallery.data?.photos[0]?.url ?? null;
  const count = TOUR_SLIDES.length;
  const last = tourHelpers.isLast(step, count);

  // Every opening starts from the first screen.
  useEffect(() => { if (open) setStep(0); }, [open]);

  const next = () => {
    const following = tourHelpers.advance(step, count);
    if (following === null) onClose();
    else setStep(following);
  };

  return (
    <Modal open={open} onOpenChange={(value) => { if (!value) onClose(); }} label="Recorrido de bienvenida">
      <div className="welcome-tour" data-testid="welcome-tour">
        <div className="welcome-backdrop" aria-hidden="true" style={photo ? { backgroundImage: `url("${photo}")` } : undefined}>
          <div className="welcome-backdrop-shade" />
        </div>

        <div className="welcome-card">
          <aside className="welcome-brand" aria-hidden="true">
            <Logo light height={30} />
            <div className="welcome-brand-copy">
              <strong>Ahorra, aparta y viaja a tu ritmo</strong>
              <p>Un recorrido de 30 segundos por todo lo que puedes hacer.</p>
            </div>
            <span className="welcome-step">
              PASO <span key={step}>{tourHelpers.stepLabel(step)}</span> <em>DE {tourHelpers.stepLabel(count - 1)}</em>
            </span>
          </aside>

          <div className="welcome-body">
            <div className="welcome-head">
              <Logo height={24} className="welcome-head-logo" />
              <button type="button" className="welcome-skip" onClick={onClose}>Omitir</button>
            </div>

            <div className="welcome-viewport">
              <div className="welcome-track" style={{ transform: `translateX(-${step * 100}%)` }}>
                {TOUR_SLIDES.map((slide, index) => (
                  <section
                    key={slide.id}
                    className="welcome-slide"
                    aria-hidden={index !== step}
                    // Hidden slides are still in the DOM for the sliding transition, but must not be reachable.
                    {...(index !== step ? { inert: true } : {})}
                  >
                    <div className="welcome-icon">{slide.icon}</div>
                    <h2>{slide.title}</h2>
                    <p>{slide.copy}</p>
                  </section>
                ))}
              </div>
            </div>

            <div className="welcome-foot">
              <div className="welcome-dots" role="tablist" aria-label="Pantallas del recorrido">
                {TOUR_SLIDES.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    role="tab"
                    aria-selected={index === step}
                    aria-label={`Pantalla ${index + 1}: ${slide.title}`}
                    className={`welcome-dot${index === step ? " active" : ""}`}
                    onClick={() => setStep(index)}
                  />
                ))}
              </div>
              <button type="button" className={`primary-button welcome-cta${last ? "" : " solid"}`} onClick={next}>
                <span className="button-text" key={last ? "last" : "next"}>{tourHelpers.ctaLabel(step, count)}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
